import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import { useParams } from "react-router-dom";

import ZoneFormModal from "./ZoneFormModal";
import Button from "../../../../../components/ui/Button";
import Card from "../../../../../components/ui/Card";
import ConfirmDeleteModal from "../../../../../components/ui/ConfirmDeleteModal";
import { useTheme } from "../../../../../context/ThemeContext";
import { useBranch } from "../../../../../context/BranchContext";
import { addNotification } from "../../../../../store/slices/uiSlice";
import { Zone, ZoneFormData } from "../../../../../types/zone";
import { useAppDispatch } from "../../../../../store/hooks";
import { useZones } from "../../../../../hooks/useZones";
import { formatPriceWithCurrency } from "../../../../../utils/formatters";

const ZonesPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const { branchId } = useParams<{ branchId: string }>();
    const { selectedCurrency } = useBranch();

    const locale = i18n?.language || "en-US";
    const formatFee = useCallback(
        (amount: number) => formatPriceWithCurrency(amount, locale, selectedCurrency),
        [locale, selectedCurrency]
    );

    const { zones, isLoading, addZone, editZone, removeZone } = useZones(branchId);

    const [editingZone, setEditingZone] = useState<Zone | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddZone = () => {
        setEditingZone(null);
        setFormModalOpen(true);
    };

    const handleEditZone = (zone: Zone) => {
        setEditingZone(zone);
        setFormModalOpen(true);
    };

    const handleFormSubmit = async (data: ZoneFormData) => {
        setIsSubmitting(true);
        try {
            if (editingZone) {
                await editZone(editingZone.id, data);
                dispatch(addNotification({
                    type: "success",
                    title: t("menuManagement.zones.updateSuccess"),
                    message: t("menuManagement.zones.updateSuccess"),
                    duration: 3000
                }));
            } else {
                await addZone(data);
                dispatch(addNotification({
                    type: "success",
                    title: t("menuManagement.zones.addSuccess"),
                    message: t("menuManagement.zones.addSuccess"),
                    duration: 3000
                }));
            }
            setFormModalOpen(false);
            setEditingZone(null);
        } catch (error: any) {
            dispatch(addNotification({
                type: "error",
                title: t("common.error"),
                message: error.message || t("common.error"),
                duration: 3000
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (zone: Zone) => {
        setZoneToDelete(zone);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!zoneToDelete) return;

        setIsDeleting(true);
        try {
            await removeZone(zoneToDelete.id);
            setDeleteModalOpen(false);
            setZoneToDelete(null);
            dispatch(addNotification({
                type: "success",
                title: t("menuManagement.zones.deleteSuccess"),
                message: t("menuManagement.zones.deleteSuccess"),
                duration: 3000
            }));
        } catch (error: any) {
            dispatch(addNotification({
                type: "error",
                title: t("common.error"),
                message: error.message || t("common.error"),
                duration: 3000
            }));
        } finally {
            setIsDeleting(false);
        }
    };

    const getDisplayName = (zone: Zone) => {
        return i18n.language === "ar" ? zone.ar_zone_name : zone.zone_name;
    };

    return (
        <div
            className={`px-4 sm:px-6 lg:px-8 py-8 ${theme === "dark" ? "text-gray-100" : "text-gray-900"
                }`}
        >
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {t("menuManagement.zones.title")}
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        {t("menuManagement.zones.subtitle")}
                    </p>
                </div>

                {/* Add Button */}
                <div className="mb-6">
                    <Button
                        onClick={handleAddZone}
                        leftIcon={<FiPlus />}
                        className="bg-primary hover:bg-primary/90"
                    >
                        {t("menuManagement.zones.addZone")}
                    </Button>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : zones.length === 0 ? (
                <Card className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400 text-lg">
                        {t("menuManagement.zones.noZones")}
                    </p>
                </Card>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className={theme === "dark" ? "bg-gray-800" : "bg-gray-50"}>
                            <tr>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("menuManagement.zones.zoneName")}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("menuManagement.zones.arZoneName")}
                                </th>
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("menuManagement.zones.fee")}
                                </th>
                                <th className="px-6 py-3 text-end text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("common.actions")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${theme === "dark" ? "divide-gray-700 bg-gray-900" : "divide-gray-200 bg-white"}`}>
                            {zones.map((zone) => (
                                <tr key={zone.id} className={theme === "dark" ? "hover:bg-gray-800" : "hover:bg-gray-50"}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {zone.zone_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" dir="rtl">
                                        {zone.ar_zone_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {formatFee(zone.fee)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditZone(zone)}
                                                leftIcon={<FiEdit className="w-4 h-4" />}
                                            >
                                                {t("common.edit")}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                                                onClick={() => handleDeleteClick(zone)}
                                                leftIcon={<FiTrash2 className="w-4 h-4" />}
                                            >
                                                {t("common.delete")}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onCancel={() => {
                    setDeleteModalOpen(false);
                    setZoneToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t("menuManagement.zones.deleteZone")}
                message={t("menuManagement.zones.deleteConfirmation")}
                isLoading={isDeleting}
            />

            {/* Add/Edit Form Modal */}
            <ZoneFormModal
                isOpen={formModalOpen}
                onClose={() => {
                    setFormModalOpen(false);
                    setEditingZone(null);
                }}
                onSubmit={handleFormSubmit}
                initialData={editingZone}
                isLoading={isSubmitting}
            />
        </div>
    );
};

export default ZonesPage;
